import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../layout/Layout";
import { Box, Card, Grid, MenuItem, Select, Typography, Avatar, Table, TableBody, TableCell, TableHead, TableRow, Button, Chip, TableContainer } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import axios from "axios";
import {
  config,
  purchaseOrderAPI,
  projectMasterAPI,
  vendorAPI,
  manageCompaniesAPI,
} from "../../config";

// Material Icons
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BusinessIcon from "@mui/icons-material/Business";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";

function PurchaseOrderDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);

  // Dropdown list states
  const [projectList, setProjectList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [companyList, setCompanyList] = useState([]);

  // Filter states
  const [selectedProject, setSelectedProject] = useState("All");
  const [selectedVendor, setSelectedVendor] = useState("All");
  const [selectedCompany, setSelectedCompany] = useState("All");
  const [dateFilter, setDateFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [topItems, setTopItems] = useState([
    { item_name: "P SAND", count: 70, total_quantity: 360 },
    { item_name: "ROUGH STONE", count: 18, total_quantity: 95 },
    { item_name: "SOLID BLOCK 6\"", count: 16, total_quantity: 82 },
    { item_name: "SOLID BLOCK 4\"", count: 13, total_quantity: 65 },
    { item_name: "DUST", count: 11, total_quantity: 50 },
  ]);
  const [bottomItems, setBottomItems] = useState([
    { item_name: "GLUE", count: 1, total_quantity: 5 },
    { item_name: "TILES", count: 1, total_quantity: 12 },
    { item_name: "CEMENT", count: 2, total_quantity: 15 },
    { item_name: "BRICKS", count: 2, total_quantity: 40 },
    { item_name: "STEEL", count: 3, total_quantity: 8 },
  ]);

  useEffect(() => {
    axios({
      method: "POST",
      url: `${purchaseOrderAPI}/dashboard-items`,
      headers: config.headers,
      data: JSON.stringify({
        selectedProject,
        selectedVendor,
        selectedCompany,
        dateFilter,
      }),
    })
      .then((res) => {
        if (res.data && res.data.success && res.data.result) {
          if (res.data.result.topItems) {
            setTopItems(res.data.result.topItems);
          }
          if (res.data.result.bottomItems) {
            setBottomItems(res.data.result.bottomItems);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load dashboard items:", err);
      });
  }, [selectedProject, selectedVendor, selectedCompany, dateFilter]);

  // Load static filter options
  useEffect(() => {
    axios({ ...config, method: "POST", data: JSON.stringify({ intType: 6 }), url: projectMasterAPI })
      .then((res) => setProjectList(res.data.result || []))
      .catch((err) => console.error("Projects fetch failed", err));

    axios({ ...config, method: "POST", data: JSON.stringify({ intType: 3 }), url: vendorAPI })
      .then((res) => setVendorList(res.data.result || []))
      .catch((err) => console.error("Vendors fetch failed", err));

    axios({ ...config, method: "POST", data: JSON.stringify({ intType: 5 }), url: manageCompaniesAPI })
      .then((res) => setCompanyList(res.data.result || []))
      .catch((err) => console.error("Companies fetch failed", err));
  }, []);

  // Fetch PO / WO list
  const fetchAllData = () => {
    setLoading(true);
    const requestData = JSON.stringify({
      txtOrderType: 0,
      txtPODate: "2000-2050",
      txtCompanyName: 0,
      txtSearchString: "",
      txtSortDirection: "desc",
      intLimit: 100000,
      intOffset: 0,
      intType: 23,
      isDashboard: true,
    });

    axios({ ...config, method: "POST", data: requestData, url: purchaseOrderAPI })
      .then((res) => setAllOrders(res.data.data || []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...allOrders];

    if (selectedProject !== "All") {
      filtered = filtered.filter((o) => o.project_name === selectedProject);
    }
    if (selectedVendor !== "All") {
      filtered = filtered.filter((o) => o.vendor_name === selectedVendor);
    }
    if (selectedCompany !== "All") {
      filtered = filtered.filter((o) => o.company_Name === selectedCompany);
    }

    if (dateFilter !== "All") {
      const today = moment();
      filtered = filtered.filter((o) => {
        if (!o.po_date) return false;
        const poDate = moment(o.po_date);
        if (dateFilter === "Today") return poDate.isSame(today, "day");
        if (dateFilter === "This Week") return poDate.isSame(today, "week");
        if (dateFilter === "This Month") return poDate.isSame(today, "month");
        if (dateFilter === "This Year") return poDate.isSame(today, "year");
        return true;
      });
    }

    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          (o.po_no && o.po_no.toLowerCase().includes(query)) ||
          (o.project_name && o.project_name.toLowerCase().includes(query)) ||
          (o.vendor_name && o.vendor_name.toLowerCase().includes(query))
      );
    }

    setFilteredOrders(filtered);
  }, [allOrders, selectedProject, selectedVendor, selectedCompany, dateFilter, searchQuery]);

  // Metrics (PO and WO values and counts)
  const metrics = useMemo(() => {
    let poValue = 0;
    let woValue = 0;
    let poCount = 0;
    let woCount = 0;
    const uniqueProjects = new Set();

    filteredOrders.forEach((o) => {
      const val = Number(o.total_with_gst || 0);
      if (o.status !== 100) {
        if (Number(o.order_type) === 1) {
          poValue += val;
          poCount++;
        } else if (Number(o.order_type) === 2) {
          woValue += val;
          woCount++;
        }
        if (o.project_name) uniqueProjects.add(o.project_name);
      }
    });

    const totalValue = poValue + woValue;
    const totalCount = poCount + woCount;
    const avgOrderValue = totalCount > 0 ? totalValue / totalCount : 0;

    return {
      totalValue,
      totalCount,
      poValue,
      woValue,
      poCount,
      woCount,
      activeProjects: uniqueProjects.size,
      avgOrderValue,
    };
  }, [filteredOrders]);

  const formatCr = (val) => {
    return `₹ ${(val / 10000000).toFixed(2)} Cr`;
  };

  const formatAmount = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  // PO & WO Value Trend
  const trendData = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      months.push(moment().subtract(i, "months").format("MMM YYYY"));
    }
    const poVals = Array(6).fill(0);
    const woVals = Array(6).fill(0);
    const poCounts = Array(6).fill(0);
    const woCounts = Array(6).fill(0);
    const orderCounts = Array(6).fill(0);

    filteredOrders.forEach((o) => {
      if (o.status === 100 || !o.po_date) return;
      const poMonth = moment(o.po_date).format("MMM YYYY");
      const idx = months.indexOf(poMonth);
      if (idx !== -1) {
        const val = Number(o.total_with_gst || 0);
        orderCounts[idx]++;
        if (Number(o.order_type) === 1) {
          poVals[idx] += val;
          poCounts[idx]++;
        } else if (Number(o.order_type) === 2) {
          woVals[idx] += val;
          woCounts[idx]++;
        }
      }
    });

    return {
      categories: months,
      poValues: poVals.map((v) => Number((v / 10000000).toFixed(2))),
      woValues: woVals.map((v) => Number((v / 10000000).toFixed(2))),
      counts: orderCounts,
      poCounts,
      woCounts,
    };
  }, [filteredOrders]);

  // Average Order Value Trend (in Lakhs)
  const aovTrendData = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      months.push(moment().subtract(i, "months").format("MMM YYYY"));
    }
    const monthlySums = Array(6).fill(0);
    const monthlyCounts = Array(6).fill(0);

    filteredOrders.forEach((o) => {
      if (o.status === 100 || !o.po_date) return;
      const poMonth = moment(o.po_date).format("MMM YYYY");
      const idx = months.indexOf(poMonth);
      if (idx !== -1) {
        monthlySums[idx] += Number(o.total_with_gst || 0);
        monthlyCounts[idx]++;
      }
    });

    const aovValues = monthlySums.map((sum, i) => {
      const count = monthlyCounts[i];
      return count > 0 ? Number((sum / count / 100000).toFixed(2)) : 0; // average amount in Lakhs
    });

    return {
      categories: months,
      values: aovValues,
    };
  }, [filteredOrders]);

  // Project Breakdown Top 6
  const projectBreakdown = useMemo(() => {
    const pMap = {};
    filteredOrders.forEach((o) => {
      if (o.status === 100) return;
      const p = o.project_name || "Others";
      pMap[p] = (pMap[p] || 0) + Number(o.total_with_gst || 0);
    });

    const sorted = Object.entries(pMap)
      .map(([name, val]) => ({ name, val: Number((val / 10000000).toFixed(2)) }))
      .sort((a, b) => b.val - a.val)
      .slice(0, 6);

    return {
      names: sorted.map((s) => s.name),
      values: sorted.map((s) => s.val),
    };
  }, [filteredOrders]);

  // PO Category Distribution
  const poCategoryBreakdown = useMemo(() => {
    const catMap = {
      "Steel & Structural": 0,
      "Cement & Concrete": 0,
      "Electrical": 0,
      "HVAC & Plumbing": 0,
      "Finishes": 0,
      "Others": 0,
    };
    filteredOrders.forEach((o) => {
      if (o.status === 100 || Number(o.order_type) !== 1) return;
      const val = Number(o.total_with_gst || 0);
      const nameStr = `${o.project_name} ${o.vendor_name}`.toLowerCase();
      if (nameStr.includes("steel") || nameStr.includes("metal") || nameStr.includes("iron")) {
        catMap["Steel & Structural"] += val;
      } else if (nameStr.includes("cement") || nameStr.includes("concrete") || nameStr.includes("sand")) {
        catMap["Cement & Concrete"] += val;
      } else if (nameStr.includes("electric") || nameStr.includes("cable") || nameStr.includes("wire")) {
        catMap["Electrical"] += val;
      } else if (nameStr.includes("plumb") || nameStr.includes("pipe") || nameStr.includes("ac") || nameStr.includes("hvac")) {
        catMap["HVAC & Plumbing"] += val;
      } else if (nameStr.includes("paint") || nameStr.includes("tile") || nameStr.includes("finish")) {
        catMap["Finishes"] += val;
      } else {
        catMap["Others"] += val;
      }
    });

    return {
      labels: Object.keys(catMap),
      values: Object.values(catMap).map((v) => Number((v / 10000000).toFixed(2))),
    };
  }, [filteredOrders]);

  // WO Category Distribution
  const woCategoryBreakdown = useMemo(() => {
    const catMap = {
      "Steel & Structural": 0,
      "Cement & Concrete": 0,
      "Electrical": 0,
      "HVAC & Plumbing": 0,
      "Finishes": 0,
      "Others": 0,
    };
    filteredOrders.forEach((o) => {
      if (o.status === 100 || Number(o.order_type) !== 2) return;
      const val = Number(o.total_with_gst || 0);
      const nameStr = `${o.project_name} ${o.vendor_name}`.toLowerCase();
      if (nameStr.includes("steel") || nameStr.includes("metal") || nameStr.includes("iron")) {
        catMap["Steel & Structural"] += val;
      } else if (nameStr.includes("cement") || nameStr.includes("concrete") || nameStr.includes("sand")) {
        catMap["Cement & Concrete"] += val;
      } else if (nameStr.includes("electric") || nameStr.includes("cable") || nameStr.includes("wire")) {
        catMap["Electrical"] += val;
      } else if (nameStr.includes("plumb") || nameStr.includes("pipe") || nameStr.includes("ac") || nameStr.includes("hvac")) {
        catMap["HVAC & Plumbing"] += val;
      } else if (nameStr.includes("paint") || nameStr.includes("tile") || nameStr.includes("finish")) {
        catMap["Finishes"] += val;
      } else {
        catMap["Others"] += val;
      }
    });

    return {
      labels: Object.keys(catMap),
      values: Object.values(catMap).map((v) => Number((v / 10000000).toFixed(2))),
    };
  }, [filteredOrders]);

  // Site Engineer Spend breakdown
  const engineerBreakdown = useMemo(() => {
    const engMap = {};
    filteredOrders.forEach((o) => {
      if (o.status === 100) return;
      const eng = o.site_eng_name || "Others";
      engMap[eng] = (engMap[eng] || 0) + Number(o.total_with_gst || 0);
    });

    const sorted = Object.entries(engMap)
      .map(([name, val]) => ({ name, val: Number((val / 10000000).toFixed(2)) }))
      .sort((a, b) => b.val - a.val)
      .slice(0, 5);

    return {
      names: sorted.map((s) => s.name),
      values: sorted.map((s) => s.val),
    };
  }, [filteredOrders]);

  // Top 5 Vendors
  const topVendors = useMemo(() => {
    const vMap = {};
    filteredOrders.forEach((o) => {
      if (o.status === 100) return;
      const v = o.vendor_name || "Unknown Vendor";
      vMap[v] = (vMap[v] || 0) + Number(o.total_with_gst || 0);
    });

    const totalAll = Object.values(vMap).reduce((sum, val) => sum + val, 0);

    return Object.entries(vMap)
      .map(([name, val]) => ({
        name,
        valCr: (val / 10000000).toFixed(2),
        pct: totalAll > 0 ? ((val / totalAll) * 100).toFixed(1) + "%" : "0%",
      }))
      .sort((a, b) => b.valCr - a.valCr)
      .slice(0, 5);
  }, [filteredOrders]);

  const handleExportExcel = () => {
    console.log("Excel Export triggered");
  };

  const handleExportPDF = () => {
    window.print();
  };

  // ECG Area Chart options for Top 5 Items
  const topEcgSeries = [
    {
      name: "Orders Count",
      data: topItems.map(item => Number(item.count) || 0),
    }
  ];

  const topEcgOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      sparkline: { enabled: false },
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
      }
    },
    grid: {
      borderColor: "#f1f5f9",
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    colors: ["#10b981"],
    xaxis: {
      categories: topItems.map(item => item.item_name || ""),
      labels: {
        style: { fontSize: "11px", fontWeight: 600 }
      }
    },
    tooltip: {
      y: {
        formatter: (val, opts) => {
          const item = topItems[opts.dataPointIndex];
          const qty = item && item.total_quantity ? ` (Qty: ${item.total_quantity})` : "";
          return `${val} orders${qty}`;
        }
      }
    }
  };

  // ECG Area Chart options for Bottom 5 Items
  const bottomEcgSeries = [
    {
      name: "Orders Count",
      data: bottomItems.map(item => Number(item.count) || 0),
    }
  ];

  const bottomEcgOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      sparkline: { enabled: false },
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
      }
    },
    grid: {
      borderColor: "#f1f5f9",
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    colors: ["#ef4444"],
    xaxis: {
      categories: bottomItems.map(item => item.item_name || ""),
      labels: {
        style: { fontSize: "11px", fontWeight: 600 }
      }
    },
    tooltip: {
      y: {
        formatter: (val, opts) => {
          const item = bottomItems[opts.dataPointIndex];
          const qty = item && item.total_quantity ? ` (Qty: ${item.total_quantity})` : "";
          return `${val} orders${qty}`;
        }
      }
    }
  };

  return (
    <Layout>
      <Box sx={{ pt: 10, px: 3, pb: 4, backgroundColor: "#f8fafc", minHeight: "100vh" }}>
        {/* Header section */}
        <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
          <Grid item xs={12} lg={8}>
            <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ mb: 0.5 }}>
              Purchase Order & Work Order Dashboard
            </Typography>
            <Typography variant="body2" color="#64748b">
              Super Admin Control Panel / PO & WO Analytics
            </Typography>
          </Grid>

          {/* Action buttons */}
          <Grid item xs={12} lg={4} sx={{ display: "flex", gap: "10px", justifyContent: { xs: "flex-start", lg: "flex-end" }, alignItems: "center" }}>
            <Button variant="contained" onClick={handleExportExcel} startIcon={<DownloadIcon />} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: "600", fontSize: "12px", background: "linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)" }}>
              Export Excel
            </Button>

            <Button variant="outlined" onClick={handleExportPDF} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: "600", fontSize: "12px", color: "#0f172a", borderColor: "#64748b", background: "#fff" }}>
              Print Report
            </Button>
          </Grid>
        </Grid>

        {/* Global Filter Bar */}
        <Box sx={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "16px", p: 2, mb: 4, boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={4} md={3}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: "600", mb: 0.5, display: "block" }}>Date Period</Typography>
              <Select fullWidth size="small" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} sx={{ height: "35px", fontSize: "12px", borderRadius: "8px" }}>
                <MenuItem value="All">All Time</MenuItem>
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="This Week">This Week</MenuItem>
                <MenuItem value="This Month">This Month</MenuItem>
                <MenuItem value="This Year">This Year</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={6} sm={4} md={3}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: "600", mb: 0.5, display: "block" }}>Project Portfolio</Typography>
              <Select fullWidth size="small" value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} sx={{ height: "35px", fontSize: "12px", borderRadius: "8px" }}>
                <MenuItem value="All">All Projects</MenuItem>
                {projectList.map((p) => (
                  <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={6} sm={4} md={3}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: "600", mb: 0.5, display: "block" }}>Primary Vendor</Typography>
              <Select fullWidth size="small" value={selectedVendor} onChange={(e) => setSelectedVendor(e.target.value)} sx={{ height: "35px", fontSize: "12px", borderRadius: "8px" }}>
                <MenuItem value="All">All Vendors</MenuItem>
                {vendorList.map((v) => (
                  <MenuItem key={v.id} value={v.name}>{v.name}</MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={4} md={3} display="flex" alignItems="flex-end">
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setSelectedProject("All");
                  setSelectedVendor("All");
                  setSelectedCompany("All");
                  setDateFilter("All");
                  setSearchQuery("");
                  fetchAllData();
                }}
                startIcon={<RefreshIcon />}
                sx={{ height: "35px", textTransform: "none", borderRadius: "8px", borderColor: "#64748b", color: "#0f172a", whiteSpace: "nowrap" }}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* EXECUTIVE KPI CARDS */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight="600">TOTAL PO VALUE</Typography>
                  <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ mt: 0.5 }}>{formatCr(metrics.poValue)}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: "12px", background: "rgba(37,99,235,0.1)", color: "#2563eb" }}>
                  <FolderOpenIcon />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: "#10b981", display: "flex", alignItems: "center", gap: 0.5, mt: 1.5, fontWeight: "700" }}>
                <TrendingUpIcon fontSize="inherit" /> {metrics.poCount} POs Generated
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight="600">TOTAL WO VALUE</Typography>
                  <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ mt: 0.5 }}>{formatCr(metrics.woValue)}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: "12px", background: "rgba(168,85,247,0.1)", color: "#a855f7" }}>
                  <LocalShippingIcon />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: "#a855f7", display: "flex", alignItems: "center", gap: 0.5, mt: 1.5, fontWeight: "700" }}>
                <TrendingUpIcon fontSize="inherit" /> {metrics.woCount} WOs Generated
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight="600">COMBINED SPEND</Typography>
                  <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ mt: 0.5 }}>{formatCr(metrics.totalValue)}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: "12px", background: "rgba(16,185,129,0.1)", color: "#10b981" }}>
                  <ShoppingCartIcon />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 1.5, fontWeight: "600" }}>
                Avg Order Size: {formatAmount(metrics.avgOrderValue)}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight="600">ACTIVE PROJECTS</Typography>
                  <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ mt: 0.5 }}>{metrics.activeProjects}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: "12px", background: "rgba(245,158,11,0.1)", color: "#f59e0b" }}>
                  <BusinessIcon />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 1.5, fontWeight: "600" }}>
                Across {metrics.totalCount} total orders
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* CHARTS ROW */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* PO vs WO spend trend */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2 }}>
                Monthly PO & WO Spend Trend (₹ Cr)
              </Typography>
              <ReactApexChart
                options={{
                  chart: { type: "bar", toolbar: { show: false } },
                  plotOptions: { bar: { horizontal: false, columnWidth: "50%", borderRadius: 4 } },
                  dataLabels: { enabled: false },
                  grid: { borderColor: "#f1f5f9" },
                  colors: ["#2563eb", "#a855f7"],
                  xaxis: { categories: trendData.categories },
                  yaxis: { title: { text: "Value (₹ Cr)" } },
                }}
                series={[
                  { name: "Purchase Orders (PO)", data: trendData.poValues },
                  { name: "Work Orders (WO)", data: trendData.woValues },
                ]}
                type="bar"
                height={350}
              />
            </Card>
          </Grid>
        </Grid>

        {/* CATEGORY CHARTS ROW */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* PO Category distribution */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 3 }}>
                PO Value by Category
              </Typography>
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ flexGrow: 1, minHeight: 400 }}>
                {poCategoryBreakdown.values.some(v => v > 0) ? (
                  <ReactApexChart
                    options={{
                      chart: { type: "pie" },
                      labels: poCategoryBreakdown.labels,
                      colors: ["#3b82f6", "#10b981", "#f59e0b", "#a855f7", "#ec4899", "#64748b"],
                      legend: { position: "bottom" },
                      dataLabels: {
                        enabled: true,
                        style: {
                          fontSize: "11px",
                          fontWeight: "bold",
                        },
                        background: {
                          enabled: true,
                          foreColor: "#000",
                          borderRadius: 4,
                          padding: 4,
                          opacity: 0.9,
                          borderWidth: 1,
                          borderColor: "#e2e8f0"
                        }
                      }
                    }}
                    series={poCategoryBreakdown.values}
                    type="pie"
                    height={400}
                    style={{ width: "100%" }}
                  />
                ) : (
                  <Typography align="center" color="text.secondary">No PO category records</Typography>
                )}
              </Box>
            </Card>
          </Grid>

          {/* WO Category distribution */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 3 }}>
                WO Value by Category
              </Typography>
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ flexGrow: 1, minHeight: 400 }}>
                {woCategoryBreakdown.values.some(v => v > 0) ? (
                  <ReactApexChart
                    options={{
                      chart: { type: "pie" },
                      labels: woCategoryBreakdown.labels,
                      colors: ["#8b5cf6", "#f97316", "#06b6d4", "#f43f5e", "#10b981", "#475569"],
                      legend: { position: "bottom" },
                      dataLabels: {
                        enabled: true,
                        style: {
                          fontSize: "11px",
                          fontWeight: "bold",
                        },
                        background: {
                          enabled: true,
                          foreColor: "#000",
                          borderRadius: 4,
                          padding: 4,
                          opacity: 0.9,
                          borderWidth: 1,
                          borderColor: "#e2e8f0"
                        }
                      }
                    }}
                    series={woCategoryBreakdown.values}
                    type="pie"
                    height={400}
                    style={{ width: "100%" }}
                  />
                ) : (
                  <Typography align="center" color="text.secondary">No WO category records</Typography>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* PROJECT & VENDORS ROW */}
        <Grid container spacing={3} sx={{ mb: 4 }} alignItems="stretch">
          {/* Project breakdown */}
          <Grid item xs={12} lg={7} sx={{ display: "flex" }}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", width: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2 }}>
                Order Value by Project (Top 6)
              </Typography>
              <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                <ReactApexChart
                  options={{
                    chart: { type: "bar", toolbar: { show: false } },
                    plotOptions: { bar: { horizontal: true, barHeight: "50%", borderRadius: 4 } },
                    dataLabels: { enabled: false },
                    xaxis: { categories: projectBreakdown.names },
                    colors: ["#3b82f6"],
                    tooltip: { y: { formatter: (val) => `${val} Cr` } },
                  }}
                  series={[{ name: "Value (₹ Cr)", data: projectBreakdown.values }]}
                  type="bar"
                  height={260}
                  style={{ width: "100%" }}
                />
              </Box>
            </Card>
          </Grid>

          {/* Top Vendors Table */}
          <Grid item xs={12} lg={5} sx={{ display: "flex" }}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", width: "100%", display: "flex", flexDirection: "column" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2 }}>
                Top 5 Vendors by Order Value
              </Typography>
              <TableContainer sx={{ flexGrow: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "700", py: 1 }}>Vendor</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "700", py: 1 }}>Value (₹ Cr)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "700", py: 1 }}>% of Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topVendors.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ py: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 }}>{row.name}</TableCell>
                        <TableCell align="right" sx={{ py: 1, fontWeight: "600" }}>₹ {row.valCr} Cr</TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>{row.pct}</TableCell>
                      </TableRow>
                    ))}
                    {topVendors.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 2 }}>No vendor records found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>

        {/* TOP & BOTTOM ITEMS ROW */}
        <Grid container spacing={3} sx={{ mb: 4 }} alignItems="stretch">
          {/* Top 5 Items Chart */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", width: "100%", display: "flex", flexDirection: "column" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 0.5 }}>
                Top 5 Highly added purchase ordered items
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                Most frequently added items in Purchase Orders
              </Typography>
              <Box sx={{ flexGrow: 1, minHeight: 280, display: "flex", alignItems: "center" }}>
                {topItems.length > 0 ? (
                  <ReactApexChart
                    options={topEcgOptions}
                    series={topEcgSeries}
                    type="area"
                    height={260}
                    style={{ width: "100%" }}
                  />
                ) : (
                  <Typography align="center" color="text.secondary" sx={{ width: "100%" }}>No item data available</Typography>
                )}
              </Box>
            </Card>
          </Grid>

          {/* Bottom 5 Items Chart */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", width: "100%", display: "flex", flexDirection: "column" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 0.5 }}>
                Bottom 5 Least added purchase ordered items
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                Least added items in Purchase Orders
              </Typography>
              <Box sx={{ flexGrow: 1, minHeight: 280, display: "flex", alignItems: "center" }}>
                {bottomItems.length > 0 ? (
                  <ReactApexChart
                    options={bottomEcgOptions}
                    series={bottomEcgSeries}
                    type="area"
                    height={260}
                    style={{ width: "100%" }}
                  />
                ) : (
                  <Typography align="center" color="text.secondary" sx={{ width: "100%" }}>No item data available</Typography>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* BOTTOM METRICS ROW (Average Order Value & Monthly order volume by type) */}
        <Grid container spacing={3}>
          {/* Average Order Value Trend */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2 }}>
                Average Order Size Trend (₹ Lakhs)
              </Typography>
              <Box sx={{ minHeight: 280, display: "flex", alignItems: "center" }}>
                {aovTrendData.values.some(v => v > 0) ? (
                  <ReactApexChart
                    options={{
                      chart: { type: "line", toolbar: { show: false } },
                      stroke: { curve: "smooth", width: 3 },
                      grid: { borderColor: "#f1f5f9" },
                      xaxis: { categories: aovTrendData.categories },
                      colors: ["#3b82f6"],
                      yaxis: { title: { text: "Average Value (₹ L)" } },
                    }}
                    series={[{ name: "Avg Value (Lakhs)", data: aovTrendData.values }]}
                    type="line"
                    height={260}
                    style={{ width: "100%" }}
                  />
                ) : (
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>No order records found</Typography>
                )}
              </Box>
            </Card>
          </Grid>

          {/* Monthly Counts Trend split by PO & WO */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: "18px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 2 }}>
                Monthly Order Volume by Type (Counts)
              </Typography>
              <Box sx={{ minHeight: 280, display: "flex", alignItems: "center" }}>
                {trendData.counts.some(c => c > 0) ? (
                  <ReactApexChart
                    options={{
                      chart: { type: "line", toolbar: { show: false } },
                      stroke: { curve: "smooth", width: 3 },
                      grid: { borderColor: "#f1f5f9" },
                      xaxis: { categories: trendData.categories },
                      colors: ["#10b981", "#a855f7"],
                      yaxis: { title: { text: "Orders Raised" } },
                    }}
                    series={[
                      { name: "Purchase Orders (PO)", data: trendData.poCounts },
                      { name: "Work Orders (WO)", data: trendData.woCounts },
                    ]}
                    type="line"
                    height={260}
                    style={{ width: "100%" }}
                  />
                ) : (
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>No monthly trend data</Typography>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
}

export default PurchaseOrderDashboard;
